import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SITE } from "../data/seoContent";

// Sets page-level SEO/AEO metadata imperatively — title, description, canonical,
// Open Graph, Twitter cards, robots, and an optional JSON-LD schema blob.
//
// Usage: <SEO title description path image schema />  (place once per page)
//
// schema may be a single object or an array of JSON-LD objects (e.g.
// BreadcrumbList + Service + FAQPage combined).

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const SCHEMA_ID = "gs-jsonld-page";

export default function SEO({
  title,
  description,
  path,
  image,
  keywords,
  schema,
  noindex = false,
}) {
  const location = useLocation();

  useEffect(() => {
    const pageTitle = title ? `${title}` : SITE.defaultTitle;
    const pageDesc = description || SITE.defaultDescription;
    const pagePath = path ?? location.pathname;
    const url = `${SITE.domain}${pagePath === "/" ? "" : pagePath}`;
    const ogImage = image || `${SITE.domain}/og-default.jpg`;

    document.title = pageTitle;
    setMeta("description", pageDesc);
    setMeta("keywords", keywords || SITE.keywords);
    setMeta("robots", noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large");
    setLink("canonical", url);

    // Open Graph
    setMeta("og:title", pageTitle, "property");
    setMeta("og:description", pageDesc, "property");
    setMeta("og:url", url, "property");
    setMeta("og:type", "website", "property");
    setMeta("og:site_name", SITE.name, "property");
    setMeta("og:image", ogImage, "property");

    // Twitter
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", pageTitle);
    setMeta("twitter:description", pageDesc);
    setMeta("twitter:image", ogImage);

    // JSON-LD (clean previous)
    const existing = document.getElementById(SCHEMA_ID);
    if (existing) existing.remove();
    // Always include LocalBusiness + WebSite schema on every page so Google/Perplexity/Claude
    // see consistent NAP, areaServed, geo, sameAs and a SearchAction across the entire site.
    const baseSchema = [localBusinessSchema(), webSiteSchema()];
    const provided = schema ? (Array.isArray(schema) ? schema : [schema]) : [];
    const blob = [...baseSchema, ...provided];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = SCHEMA_ID;
    script.textContent = JSON.stringify(blob.length === 1 ? blob[0] : blob);
    document.head.appendChild(script);
  }, [title, description, path, image, keywords, schema, noindex, location.pathname]);

  return null;
}

// --- schema helpers --------------------------------------------------------

export function localBusinessSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    "@id": `${SITE.domain}/#organization`,
    name: SITE.legalName,
    alternateName: ["OEC", "Oxford Environmental Consultants"],
    legalName: "Oxford Environmental Consultants Limited",
    identifier: [
      { "@type": "PropertyValue", propertyID: "Companies House", value: "11540812" },
    ],
    url: SITE.domain,
    telephone: "+443456868868",
    email: "enquiries@oxford-ec.co.uk",
    image: [
      `${SITE.domain}/og-default.jpg`,
      `${SITE.domain}/brand/oxford-ec-horizontal-web.png`,
    ],
    logo: `${SITE.domain}/brand/oxford-ec-horizontal-web.png`,
    priceRange: "££",
    address: {
      "@type": "PostalAddress",
      streetAddress: "Basildon",
      addressLocality: "Basildon",
      addressRegion: "Essex",
      postalCode: "SS14 3EQ",
      addressCountry: "GB",
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 51.5688,
      longitude: 0.4561,
    },
    openingHoursSpecification: [{
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "08:00",
      closes: "18:00",
    }],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "5.0",
      reviewCount: 1,
      bestRating: 5,
      worstRating: 1,
    },
    areaServed: [
      { "@type": "Country", name: "United Kingdom" },
      { "@type": "City", name: "London" },
      { "@type": "City", name: "Oxford" },
      { "@type": "City", name: "Cambridge" },
      { "@type": "City", name: "Reading" },
      { "@type": "City", name: "Birmingham" },
      { "@type": "City", name: "Leicester" },
      { "@type": "City", name: "Nottingham" },
    ],
    serviceType: [
      "Asbestos Survey", "Asbestos Management Survey", "Asbestos Refurbishment Survey", "Asbestos Demolition Survey",
      "Fire Risk Assessment", "Fire Door Inspection", "Flat Entrance Door Inspection",
      "Compartmentation Survey", "Fire Stopping Survey", "Passive Fire Protection Survey",
      "Emergency Lighting Testing", "Fire Alarm Servicing",
      "Legionella Risk Assessment", "Mould Survey", "Property Compliance Services",
    ],
    knowsAbout: [
      "Control of Asbestos Regulations 2012",
      "Regulatory Reform (Fire Safety) Order 2005",
      "Fire Safety Act 2021",
      "Fire Safety (England) Regulations 2022",
      "Building Safety Act 2022",
      "Awaab's Law",
      "Social Housing (Regulation) Act 2023",
      "Housing Health and Safety Rating System (HHSRS)",
      "ACoP L8",
      "HSG 274",
      "HSG 264",
      "PAS 9980",
    ],
    hasCredential: [
      { "@type": "EducationalOccupationalCredential", name: "ISO 9001 — Quality Management" },
      { "@type": "EducationalOccupationalCredential", name: "SMAS Worksafe Certified" },
      { "@type": "EducationalOccupationalCredential", name: "SSIP Registered" },
      { "@type": "EducationalOccupationalCredential", name: "UKATA Registered" },
      { "@type": "EducationalOccupationalCredential", name: "NORAC Member — National Organisation of Asbestos Consultants" },
      { "@type": "EducationalOccupationalCredential", name: "NFRAR Registered Assessors" },
      { "@type": "EducationalOccupationalCredential", name: "BOHS P402 / P405 Licensed Surveyors" },
      { "@type": "EducationalOccupationalCredential", name: "Federation of Damp Member" },
    ],
    sameAs: [
      "https://www.linkedin.com/company/oxford-environmental-consultants-ltd/",
      "https://www.facebook.com/share/1B6hCfwjqv/",
      "https://www.instagram.com/oxfordecltd",
      "https://www.tiktok.com/@oecltd",
    ],
  };
}

export function webSiteSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE.domain}/#website`,
    url: SITE.domain,
    name: SITE.legalName,
    alternateName: "OEC",
    publisher: { "@id": `${SITE.domain}/#organization` },
    inLanguage: "en-GB",
  };
}

export function breadcrumbSchema(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE.domain}${item.path}`,
    })),
  };
}

export function serviceSchema({ name, description, path, areaServed = "United Kingdom", serviceType, offers }) {
  const node = {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url: `${SITE.domain}${path}`,
    provider: {
      "@type": "Organization",
      "@id": `${SITE.domain}/#organization`,
      name: SITE.legalName,
      url: SITE.domain,
    },
    areaServed: { "@type": "Country", name: areaServed },
  };
  if (serviceType) node.serviceType = serviceType;
  if (offers && offers.length) {
    node.hasOfferCatalog = {
      "@type": "OfferCatalog",
      name: `${name} — service catalogue`,
      itemListElement: offers.map((o) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: typeof o === "string" ? o : o.name },
      })),
    };
  }
  return node;
}

export function faqSchema(faqs) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a.replace(/<[^>]+>/g, "") },
    })),
  };
}

export function articleSchema({ headline, description, slug, image, datePublished, dateModified, basePath = "/compliance-hub" }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    image,
    datePublished,
    dateModified: dateModified || datePublished,
    author: { "@type": "Organization", name: SITE.legalName },
    publisher: {
      "@type": "Organization",
      name: SITE.legalName,
      logo: { "@type": "ImageObject", url: `${SITE.domain}/brand/oxford-ec-horizontal-web.png` },
    },
    mainEntityOfPage: `${SITE.domain}${basePath}/${slug}`,
  };
}
